import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { DeltaStatic, Quill } from "react-quill-new";
import type QuillToolbar from "quill/modules/toolbar";
import "react-quill-new/dist/quill.snow.css";
import { commonStyles } from "../commonStyles";
import { SearchableSelectField } from "../dashboard/SearchableSelectField";

// Toolbar per the create-event transcription spec (fix-pack contract §5):
// Paragraph style dropdown, bold/italic/underline, alignment, link. The
// bullet-list variant is opt-in (`withBulletList`) so callers that need lists
// (e.g. sponsorship benefits) can add them without changing this default.
const DEFAULT_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ["bold", "italic", "underline"],
    [{ align: "" }, { align: "center" }, { align: "justify" }],
    ["link"],
  ],
};

// Group order per the edit-sponsor-level mock (PAR-EW-17):
// Paragraph | B/I/U | bullet list | alignment | link.
const BULLET_LIST_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ["bold", "italic", "underline"],
    [{ list: "bullet" }],
    [{ align: "" }, { align: "center" }, { align: "justify" }],
    ["link"],
  ],
};

// Preserve source paragraph line breaks only in the automated-email variant.
// A unique class leaves Quill's default BR blot registered for other editors.
let automatedEmailLineBreakRegistered = false;
const registerAutomatedEmailLineBreak = () => {
  if (automatedEmailLineBreakRegistered) return;
  const EmailEmbed = (Quill.import("parchment") as typeof import("parchment")).EmbedBlot;
  class AutomatedEmailLineBreak extends EmailEmbed {
    static blotName = "automatedEmailLineBreak";
    static tagName = "BR";
    static className = "cmp-automated-email-line-break";
    static value() { return true; }
    html() { return "<br>"; }
  }
  Quill.register(AutomatedEmailLineBreak, true);
  automatedEmailLineBreakRegistered = true;
};

const AUTOMATED_EMAIL_MODULES = {
  clipboard: { matchers: [["br", () => {
    const Delta = Quill.import("delta") as typeof import("quill-delta").default;
    return new Delta().insert({ automatedEmailLineBreak: true });
  }]] },
  keyboard: { bindings: { softLineBreak: {
    key: "Enter", shiftKey: true,
    handler(this: { quill: ReturnType<ReactQuill["getEditor"]> }, range: { index: number; length: number }) {
      this.quill.deleteText(range.index, range.length, "user");
      this.quill.insertEmbed(range.index, "automatedEmailLineBreak", true, "user");
      this.quill.setSelection(range.index + 1, 0, "silent");
      return false;
    },
  } } },
  toolbar: [
    [{ size: ["12px", "14px", "16px", "18px", "24px"] }],
    ["bold", "italic", "underline", "strike"],
    [{ align: "" }, { align: "center" }, { align: "right" }],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
};

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const EMAIL_BUILDER_SIZES = [10, 11, 12, 13, 14, 16, 18, 20, 22, 24];

// Quill 2 preserves list structure in semantic HTML but drops the attributes
// on each list item. Restore those attributes without exporting its UI spans
// or flattening its semantic bullet, numbered, and nested lists.
export const preserveEmailListFormatting = (html: string, editorRoot: HTMLElement): string => {
  const sourceItems = Array.from(editorRoot.querySelectorAll("li"));
  if (!sourceItems.length) return html;
  const template = document.createElement("template");
  template.innerHTML = html;
  const outputItems = Array.from(template.content.querySelectorAll("li")).filter(item =>
    // Quill can add an empty parent when a saved list starts indented. That
    // structural parent has no source item and must not consume its styles.
    Array.from(item.childNodes).some(child => !(child instanceof HTMLElement) || !["OL", "UL"].includes(child.tagName)),
  );
  outputItems.forEach((item, index) => {
    const source = sourceItems[index];
    if (!source) return;
    for (const property of Array.from(source.style)) {
      item.style.setProperty(property, source.style.getPropertyValue(property));
    }
    const alignment = Array.from(source.classList).find(name => /^ql-align-(left|center|right|justify)$/.test(name));
    if (alignment) item.style.textAlign = alignment.slice("ql-align-".length);
  });
  return template.innerHTML;
};

// Quill drops an edge or double space and a last empty line when it reads HTML.
// While staff type, that loss is not a real change, so keep the document they see.
const withoutSpaces = (delta: DeltaStatic): DeltaStatic => {
  const words = delta.slice(0, 0);
  delta.ops.forEach(op => {
    if (typeof op.insert !== "string") { words.push(op); return; }
    const insert = op.insert.replace(/\s+/g, "");
    if (insert) words.insert(insert, op.attributes);
  });
  return words;
};
const sameWords = (first: DeltaStatic, second: DeltaStatic): boolean =>
  withoutSpaces(first).diff(withoutSpaces(second)).ops.length === 0;

let editorInstanceCounter = 0;

type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  maxLength?: number;
  /** false: count characters against maxLength without blocking typing. */
  enforceMaxLength?: boolean;
  /** Red border for a required field that is empty (CES:193 eb.*). */
  error?: boolean;
  minHeight?: number;
  withBulletList?: boolean;
  variant?: "default" | "automatedEmail" | "emailBuilder" | "event";
  readOnly?: boolean;
  ariaLabel?: string;
  defaultFontSize?: number;
  defaultTextAlign?: "left" | "center" | "right" | "justify";
};

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  maxLength,
  enforceMaxLength = true,
  error = false,
  minHeight,
  withBulletList = false,
  variant = "default",
  readOnly = false,
  ariaLabel = "Rich text",
  defaultFontSize = 14,
  defaultTextAlign = "left",
}: RichTextEditorProps): React.ReactElement => {
  // The approved Event and Email Builder sources use the same controls.
  const usesContentToolbar = variant === "emailBuilder" || variant === "event";
  useMemo(() => {
    if (variant === "automatedEmail") registerAutomatedEmailLineBreak();
    if (variant === "automatedEmail" || usesContentToolbar) {
      const sizeStyle = Quill.import("attributors/style/size") as import("parchment").StyleAttributor;
      // Quill's registry is shared. Extend the existing sizes so opening an
      // automated-email editor cannot remove sizes used by a builder draft.
      const savedSizes = Array.from(value.matchAll(/font-size:\s*([\d.]+(?:px|pt|em|rem|%))/gi), match => match[1]);
      sizeStyle.whitelist = Array.from(new Set([
        ...(sizeStyle.whitelist || []),
        ...EMAIL_BUILDER_SIZES.map(size => `${size}px`),
        `${defaultFontSize}px`,
        ...savedSizes,
      ]));
      Quill.register(sizeStyle, true);
      if (usesContentToolbar) {
        // Keep Quill's existing class format for other editor consumers.
        // Explicit left alignment can override a legacy field's default.
        const alignClass = Quill.import("attributors/class/align") as import("parchment").ClassAttributor;
        alignClass.whitelist = Array.from(new Set([...(alignClass.whitelist || []), "left"]));
      }
    }
  }, [variant, usesContentToolbar, value, defaultFontSize]);
  const quillRef = useRef<ReactQuill>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const savedSelection = useRef<{ index: number; length: number } | null>(null);
  const [selectedSize, setSelectedSize] = useState(`${defaultFontSize}px`);
  const [builderReady, setBuilderReady] = useState(false);
  // Monday D39 + Claude Design (16) builder text box: min 96px, grows with
  // its content and has no inner scroll, so drag-selecting text does not
  // scroll the box under the pointer.
  const editorMinHeight = minHeight ?? (variant === "emailBuilder" ? 96 : 140);
  const plainLength = useMemo(() => stripHtml(value || "").length, [value]);
  // Unique per-instance class so editors with different minHeight props do
  // not overwrite each other's style rules.
  const instanceClass = useRef(`rich-text-editor-${(editorInstanceCounter += 1)}`);

  const builderModules = useMemo(() => ({
    toolbar: {
      container: `#${instanceClass.current}-toolbar`,
      handlers: {
        align(this: { quill: ReturnType<ReactQuill["getEditor"]> }, alignment: string | false) {
          // An explicit left style must override a legacy field's alignment.
          this.quill.format("align", alignment || "left", "user");
        },
      },
    },
  }), []);

  useEffect(() => {
    if (!usesContentToolbar) return;
    const editor = quillRef.current?.getEditor();
    editor?.root?.setAttribute("aria-label", ariaLabel);
    editor?.root?.setAttribute("role", "textbox");
    editor?.root?.setAttribute("aria-multiline", "true");
    editor?.root?.setAttribute("aria-readonly", String(readOnly));
  }, [ariaLabel, readOnly, usesContentToolbar]);

  useEffect(() => {
    if (!usesContentToolbar) return;
    const editor = quillRef.current?.getEditor();
    const toolbar = toolbarRef.current;
    if (!editor || !toolbar) return;
    const left = toolbar.querySelector<HTMLButtonElement>('.ql-align[value="left"]');
    const icons = Quill.import("ui/icons") as { align: Record<string, string> };
    // Snow names its left icon "". Keep the explicit format value while
    // letting Quill own the SVG content, as it does for the other buttons.
    if (left) left.innerHTML = icons.align[""];
    const updateAlignment = () => {
      const range = editor.getSelection() || savedSelection.current;
      const format: unknown = (range ? editor.getFormat(range) : editor.getFormat(0)).align;
      const alignment = format || defaultTextAlign;
      toolbar.querySelectorAll<HTMLButtonElement>(".ql-align").forEach(button => {
        const active = alignment === button.value;
        button.classList.toggle("ql-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
    };
    updateAlignment();
    editor.on("editor-change", updateAlignment);
    return () => { editor.off("editor-change", updateAlignment); };
  }, [defaultTextAlign, usesContentToolbar]);

  useEffect(() => { setBuilderReady(usesContentToolbar); }, [usesContentToolbar]);

  // Parent HTML is sanitized and uses semantic lists. Compare its Quill
  // content, not its markup, with local edits so saving a format does not
  // replace the document and restore a stale selection.
  const editorValue = useMemo(() => {
    if (!usesContentToolbar || !builderReady) return value;
    const editor = quillRef.current?.getEditor();
    if (!editor) return value;
    const converted = editor.clipboard.convert({ html: value });
    const contents = converted.slice(0, 0);
    // Clipboard matching places paragraph formats on the full text run.
    // Quill stores those formats on newlines only. Use its format registry
    // to compare the canonical document, without maintaining a second list
    // of inline and paragraph formats.
    converted.ops.forEach(op => {
      if (typeof op.insert !== "string") { contents.push(op); return; }
      op.insert.split(/(\n)/).filter(Boolean).forEach(part => {
        const attributes = Object.fromEntries(Object.entries(op.attributes || {}).filter(([name]) => {
          const blockFormat = Boolean(editor.scroll.query(name, (Quill.import("parchment") as typeof import("parchment")).Scope.BLOCK));
          return part === "\n" ? blockFormat : !blockFormat;
        }));
        contents.insert(part, attributes);
      });
    });
    const lastInsert = contents.ops[contents.ops.length - 1]?.insert;
    // Clipboard conversion omits an unformatted final newline, while every
    // Quill document includes one. Match that canonical document form.
    if (typeof lastInsert !== "string" || !lastInsert.endsWith("\n")) contents.insert("\n");
    return editor.hasFocus() && sameWords(contents, editor.getContents()) ? editor.getContents() : contents;
  }, [builderReady, value, usesContentToolbar]);

  const rememberSelection = () => {
    const range = quillRef.current?.getEditor().getSelection();
    if (range) savedSelection.current = { index: range.index, length: range.length };
  };

  const preserveToolbarSelection = (event: React.MouseEvent<HTMLDivElement>) => {
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("button") : null;
    if (!button) return;
    event.preventDefault();
    const editor = quillRef.current?.getEditor();
    if (event.type === "click") event.stopPropagation();
    if (!editor || readOnly) return;
    const range = editor.getSelection() || savedSelection.current;
    if (range) savedSelection.current = { index: range.index, length: range.length };
    // Retain native text selection instead of moving focus onto the button.
    if (event.type === "mousedown") return;
    if (range) editor.setSelection(range.index, range.length, "silent");
    else editor.focus();
    const format = Array.from(button.classList).find(name => name.startsWith("ql-"))?.slice(3);
    if (!format) return;
    const toolbar = editor.getModule("toolbar") as QuillToolbar;
    const formatValue = button.classList.contains("ql-active") ? false : button.value || !button.hasAttribute("value");
    // ReactQuill leaves external-toolbar listeners behind after a StrictMode
    // remount. Dispatch once through the current toolbar's canonical handler,
    // before the native click can reach listeners bound to a removed editor.
    if (toolbar.handlers[format]) toolbar.handlers[format].call(toolbar, formatValue);
    else editor.format(format, formatValue, "user");
    toolbar.update(editor.getSelection());
  };

  const handleSizeChange = (size: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor || readOnly) return;
    const range = savedSelection.current || editor.getSelection(true);
    if (range) editor.setSelection(range.index, range.length, "silent");
    if (range?.length) {
      editor.formatText(range.index, range.length, "size", size, "user");
    } else {
      // The design applies a size to the whole field when no text is selected.
      editor.formatText(0, Math.max(0, editor.getLength() - 1), "size", size, "user");
      editor.format("size", size, "user");
    }
    setSelectedSize(size);
    // The shared select returns focus to its trigger when it closes. Restore
    // the selected text afterwards so the next format uses the same range.
    if (range) queueMicrotask(() => {
      if (quillRef.current?.getEditor() === editor) {
        editor.setSelection(range.index, range.length, "silent");
      }
    });
  };

  const handleSelectionChange = () => {
    const editor = quillRef.current?.getEditor();
    const range = editor?.getSelection();
    if (!editor || !range) return;
    savedSelection.current = { index: range.index, length: range.length };
    const size: unknown = editor.getFormat(range).size;
    setSelectedSize(typeof size === "string" ? size : Array.isArray(size) ? "" : `${defaultFontSize}px`);
  };

  // Quill emits the React change before its "text-change" listeners run, so
  // this snapshot still holds the document from before the current edit.
  const previousContents = useRef<DeltaStatic | null>(null);
  useEffect(() => {
    if (!maxLength) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    previousContents.current = editor.getContents();
    const rememberContents = () => { previousContents.current = editor.getContents(); };
    editor.on("text-change", rememberContents);
    return () => { editor.off("text-change", rememberContents); };
  }, [maxLength]);

  // Saved content can be longer than the current limit (for example imported
  // event descriptions). Never trim it. Reject only an edit that makes the
  // text longer while it is over the limit, so staff can still edit or
  // shorten the text. Quill's getLength includes a trailing newline.
  const rejectsLengthIncrease = (delta: DeltaStatic): boolean => {
    const editor = quillRef.current?.getEditor();
    const previous = previousContents.current;
    if (!maxLength || !enforceMaxLength || !editor || !previous) return false;
    const nextLength = editor.getLength() - 1;
    if (nextLength <= maxLength || nextLength <= previous.length() - 1) return false;
    const range = editor.getSelection();
    const undo = delta.invert(previous);
    editor.updateContents(undo, "silent");
    if (range) editor.setSelection(undo.transformPosition(range.index), 0, "silent");
    return true;
  };

  const handleChange = (html: string, delta: DeltaStatic, source: string) => {
    // Controlled value updates are already owned by the caller. Quill can
    // normalize that HTML on mount or refresh; only user edits change the form.
    if (source !== "user" || readOnly) return;
    if (rejectsLengthIncrease(delta)) return;
    const editor = quillRef.current?.getEditor();
    onChange(usesContentToolbar && editor
      ? preserveEmailListFormatting(editor.getSemanticHTML(), editor.root)
      : html);
  };

  // Show saved text over the limit in red so staff can see it and shorten it.
  const overLimit = Boolean(maxLength && plainLength > maxLength);
  const characterCount = maxLength ? (
    <p className={variant === "event"
      ? `mt-1 text-left text-[14px] leading-5 ${overLimit ? "text-red-600" : "text-[#727272]"}`
      : `rounded-b-[10px] border-t border-gray-200 bg-gray-50 px-4 py-2 text-left text-[13px] ${overLimit ? "text-red-600" : "text-gray-600"}`}>
      {/* Event design counters have no thousands comma: "{n}/2500 characters" (CES:1528, EV15-43). */}
      {variant === "event"
        ? `${plainLength}/${maxLength}`
        : `${plainLength.toLocaleString("en-CA")}/${maxLength.toLocaleString("en-CA")}`} characters
    </p>
  ) : null;

  return (
    <>
    <div className={`rich-text-editor ${instanceClass.current}`} data-member-field-error={error || undefined}>
      {usesContentToolbar && (
        <div className="email-builder-toolbar" role="toolbar" aria-label={`${ariaLabel} formatting`}>
          <div className="email-builder-size" onMouseDownCapture={rememberSelection} onFocusCapture={rememberSelection}>
            <label className="sr-only" htmlFor={`${instanceClass.current}-size`}>Font size</label>
            <SearchableSelectField
              id={`${instanceClass.current}-size`}
              options={EMAIL_BUILDER_SIZES.map(size => ({ value: `${size}px`, label: `${size}px` }))}
              value={selectedSize}
              onChange={handleSizeChange}
              placeholder={selectedSize || "Size"}
              searchPlaceholder="Size"
              disabled={readOnly}
            />
          </div>
          <div ref={toolbarRef} id={`${instanceClass.current}-toolbar`} onMouseDownCapture={preserveToolbarSelection} onClickCapture={preserveToolbarSelection}>
          <span className="ql-formats">
            <button type="button" className="ql-bold" aria-label="Bold" title="Bold" disabled={readOnly} />
            <button type="button" className="ql-italic" aria-label="Italic" title="Italic" disabled={readOnly} />
            <button type="button" className="ql-underline" aria-label="Underline" title="Underline" disabled={readOnly} />
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-list" value="bullet" aria-label="Bullet list" title="Bullet list" disabled={readOnly} />
            <button type="button" className="ql-list" value="ordered" aria-label="Numbered list" title="Numbered list" disabled={readOnly} />
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-align" value="left" aria-label="Align left" title="Align left" disabled={readOnly} />
            <button type="button" className="ql-align" value="center" aria-label="Align centre" title="Align centre" disabled={readOnly} />
            <button type="button" className="ql-align" value="right" aria-label="Align right" title="Align right" disabled={readOnly} />
            <button type="button" className="ql-align" value="justify" aria-label="Justify" title="Justify" disabled={readOnly} />
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-link" aria-label="Insert link" title="Insert link" disabled={readOnly} />
          </span>
          </div>
        </div>
      )}
      <style>{`
        .rich-text-editor { border: 1px solid #D1D5DB; border-radius: 10px; background: #fff; }
        .rich-text-editor:focus-within { border-color: ${commonStyles.cpaGold}; }
        .rich-text-editor .ql-toolbar.ql-snow { border: 0; border-bottom: 1px solid #E5E7EB; border-radius: 10px 10px 0 0; background: #F9FAFB; }
        .rich-text-editor .ql-container.ql-snow { border: 0; font-size: 0.95rem; }
        /* Editor body text is black, not inherited grey (July 9, 2026). */
        .rich-text-editor .ql-editor { color: #030712; }
        .rich-text-editor .ql-snow .ql-picker.ql-header .ql-picker-label:not([data-value])::before,
        .rich-text-editor .ql-snow .ql-picker.ql-header .ql-picker-item:not([data-value])::before { content: "Paragraph"; }
        .${instanceClass.current} .ql-picker.ql-size .ql-picker-label[data-value]::before,
        .${instanceClass.current} .ql-picker.ql-size .ql-picker-item[data-value]::before { content: attr(data-value); }
        .${instanceClass.current} .ql-container { min-height: ${editorMinHeight}px; }
        .${instanceClass.current} .ql-editor { min-height: ${editorMinHeight}px; }
        ${usesContentToolbar ? `
          .rich-text-editor.${instanceClass.current} { border-color:${error ? "#F21B1B" : "#ECECEC"}; border-radius:8px; min-width:0; }
          .${instanceClass.current} .ql-container.ql-snow { font-family:'DM Sans',sans-serif; font-size:${defaultFontSize}px; }
          .${instanceClass.current} .ql-editor { padding:12px; max-height:${variant === "event" ? "320px" : "none"};${variant === "emailBuilder" ? " height:auto; overflow-y:visible;" : ""} line-height:1.55; color:#0A0A0A; text-align:${defaultTextAlign}; overflow-wrap:anywhere; }
          .${instanceClass.current} .ql-editor p { margin:0; }
          ${variant === "event" ? `.${instanceClass.current} .ql-editor.ql-blank::before { font-style:normal; color:#9E9E9E; }` : ""}
          .${instanceClass.current} .email-builder-toolbar { padding:8px 10px; border-bottom:1px solid #ECECEC; display:flex; align-items:center; flex-wrap:wrap; gap:6px; }
          .${instanceClass.current} .ql-toolbar.ql-snow { padding:0; background:#fff; border:0; display:contents; }
          .${instanceClass.current} .email-builder-toolbar > .ql-formats { margin:0; }
          .${instanceClass.current} .ql-toolbar .ql-formats { display:inline-flex; align-items:center; gap:2px; margin:0; }
          .${instanceClass.current} .ql-toolbar .ql-formats { padding-left:6px; border-left:1px solid #ECECEC; }
          .${instanceClass.current} .ql-toolbar button { display:inline-flex; justify-content:center; align-items:center; width:24px; height:24px; padding:0; border-radius:4px; }
          .${instanceClass.current} .ql-toolbar button svg { width:14px; height:14px; }
          .${instanceClass.current} .ql-toolbar button:hover { background:#F1F3F5; }
          .${instanceClass.current} .ql-toolbar button.ql-active { background:#FEF9E7; color:#0A0A0A; }
          .${instanceClass.current} .ql-toolbar button:disabled { opacity:.45; cursor:not-allowed; }
          .${instanceClass.current} .email-builder-size { flex:0 1 92px; min-width:72px; }
          .${instanceClass.current} .email-builder-size [data-member-select-trigger] { height:${variant === "event" ? "42px" : "34px"}; padding:0 10px; border-color:#ECECEC!important; font:13px 'DM Sans',sans-serif; }
          .${instanceClass.current} .ql-editor .ql-align-left { text-align:left; }
          .${instanceClass.current} .ql-tooltip { max-width:calc(100% - 16px); white-space:normal; z-index:2; }
          .${instanceClass.current} .ql-tooltip input[type=text] { max-width:100%; }
        ` : ""}
        ${variant === "automatedEmail" ? `
          .${instanceClass.current} { border-color:#ECECEC; border-radius:8px; overflow:hidden; }
          .${instanceClass.current} .ql-container.ql-snow { font-family:'DM Sans',sans-serif; font-size:12px; }
          .${instanceClass.current} .ql-editor { padding:16px; max-height:44vh; line-height:1.6; color:#0A0A0A; }
          .${instanceClass.current} .ql-editor p { margin:12px 0; }
          .${instanceClass.current} .ql-toolbar.ql-snow { padding:6px 10px; background:#F7F7F8; border-color:#ECECEC; border-radius:0; display:flex; align-items:center; flex-wrap:wrap; gap:2px; }
          .${instanceClass.current} .ql-toolbar .ql-formats { display:inline-flex; align-items:center; margin:0 8px 0 0; }
          .${instanceClass.current} .ql-toolbar button { display:inline-flex; justify-content:center; align-items:center; width:30px; height:28px; padding:0; }
          .${instanceClass.current} .ql-toolbar button svg { width:14px; height:14px; }
          .${instanceClass.current} .ql-toolbar .ql-size { width:50px; height:30px; border:1px solid #ECECEC; border-radius:6px; background:#fff; font-size:13px; }
          .${instanceClass.current} .ql-toolbar .ql-size .ql-picker-label { padding:4px 8px; }
          .${instanceClass.current} .ql-toolbar .ql-size .ql-picker-label svg { width:12px; right:4px; }
          ${[12,14,16,18,24].map(size => `.${instanceClass.current} .ql-picker.ql-size .ql-picker-label[data-value="${size}px"]::before, .${instanceClass.current} .ql-picker.ql-size .ql-picker-item[data-value="${size}px"]::before { content:"${size}"; }`).join("\n")}
        ` : ""}
      `}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorValue}
        onChange={handleChange}
        // react-quill-new 3.8 uses Quill's semantic HTML by default. During
        // mount, that formatter can rewrite equivalent saved HTML and emit an
        // API change. Keep the editor on its stable innerHTML representation
        // so opening a form does not look like a user edit. The builder also
        // needs root HTML to detect list alignment changes that Quill omits
        // from semantic HTML. Its handler exports semantic lists afterwards.
        useSemanticHTML={variant === "automatedEmail"}
        readOnly={readOnly}
        onChangeSelection={usesContentToolbar ? handleSelectionChange : undefined}
        modules={usesContentToolbar ? builderModules : variant === "automatedEmail" ? AUTOMATED_EMAIL_MODULES : withBulletList ? BULLET_LIST_MODULES : DEFAULT_MODULES}
        placeholder={placeholder}
      />
      {variant !== "event" ? characterCount : null}
    </div>
    {variant === "event" ? characterCount : null}
    </>
  );
};

export default RichTextEditor;
